import React, { useEffect, useState } from 'react';
import { listCareContexts } from '../../services/api/hipApi';
import { formatDateIST } from '../../utils/formatIST';
import CareContextStatusChip from '../hip/CareContextStatusChip';
import { useAnyPermission } from '../../context/PermissionContext';
import { LINKED_RECORDS, ABHA_NUMBER_LABEL, ABHA_ADDRESS_LABEL } from '../../constants/abdmCopy';
import { hiTypeLabel } from '../../constants/abdmHiTypes';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';

const INITIAL_VISIBLE = 5;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Date-only visitAt shows the date only; otherwise date + time, both in IST.
function formatVisit(visitAt) {
  if (!visitAt) return '—';
  if (DATE_ONLY.test(visitAt)) return formatDateIST(`${visitAt}T00:00:00+05:30`);
  const d = new Date(visitAt);
  if (isNaN(d)) return '—';
  const time = d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true,
  });
  return `${formatDateIST(visitAt)}, ${time}`;
}

/**
 * LinkedRecordsList — records linked to the patient's ABHA (NHA M2 7.1 Kindly Note).
 * Shows the FULL ABHA number and address (owner R28: this view only). Newest first
 * (server order); first 5 shown, "+n more" expands the rest in a bounded scroll area.
 * One fetch per patientId.
 * onShowingChange(bool): true only while the full box (data loaded) is on screen — callers hide
 * their masked ABHA line then (R130: ABHA number/address shown once). Loading/error/empty → false.
 */
export default function LinkedRecordsList({ patientId, isDark, onShowingChange }) {
  const { show: abdmOn } = useAbdmEnabled();
  const allowed = useAnyPermission('ABHA_LINK', 'HIP_CARE_CONTEXT') && abdmOn; // ABDM off: nothing fetched
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [none, setNone] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!patientId || !allowed) return undefined;
    let cancelled = false;
    setData(null);
    setFailed(false);
    setNone(false);
    setExpanded(false);
    listCareContexts(patientId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => {
        if (cancelled) return;
        // 404 = no ABHA record yet; 503 = HIP not enabled for the org: nothing to show, not an error.
        if (err?.status === 404 || err?.status === 503) setNone(true);
        else setFailed(true);
      });
    return () => { cancelled = true; };
  }, [patientId, allowed]);

  const showing = Boolean(patientId && allowed && !none && !failed && data);
  useEffect(() => {
    if (!onShowingChange) return undefined;
    onShowingChange(showing);
    return () => onShowingChange(false);
  }, [showing, onShowingChange]);

  const muted = isDark ? '#94a3b8' : '#64748b';
  const strong = isDark ? '#e2e8f0' : '#1e293b';
  const box = {
    borderRadius: 8, padding: '10px 12px',
    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
    background: isDark ? '#1e293b' : '#fff',
  };

  if (!patientId || !allowed || none) return null;
  if (failed) {
    return <div style={box}><p style={{ margin: 0, fontSize: 12, color: isDark ? '#fca5a5' : '#991b1b' }}>{LINKED_RECORDS.loadError}</p></div>;
  }
  if (!data) {
    return <div style={box}><p style={{ margin: 0, fontSize: 12, color: muted }}>{LINKED_RECORDS.loading}</p></div>;
  }

  const rows = data.careContexts;
  const visible = expanded ? rows : rows.slice(0, INITIAL_VISIBLE);
  const hidden = rows.length - visible.length;

  return (
    <div style={box} data-testid="linked-records">
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: muted }}>
        {LINKED_RECORDS.title}
      </p>
      {data.abhaNumber && (
        <p style={{ margin: '4px 0 0', fontSize: 12, color: muted }}>
          {ABHA_NUMBER_LABEL} <span style={{ fontFamily: 'monospace', color: strong }}>{data.abhaNumber}</span>
        </p>
      )}
      {data.abhaAddress && (
        <p style={{ margin: '2px 0 0', fontSize: 12, color: muted }}>
          {ABHA_ADDRESS_LABEL} <span style={{ fontFamily: 'monospace', color: strong }}>{data.abhaAddress}</span>
        </p>
      )}
      {rows.length === 0 ? (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: muted }}>{LINKED_RECORDS.empty}</p>
      ) : (
        <>
          <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, maxHeight: 240, overflowY: 'auto' }}>
            {visible.map((c) => (
              <li key={c.careContextId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 0', fontSize: 12, color: strong }}>
                <span style={{ fontWeight: 600 }}>{hiTypeLabel(c.hiType)}</span>
                <span style={{ color: muted }}>{formatVisit(c.visitAt)}</span>
                <CareContextStatusChip status={c.linkStatus} isDark={isDark} />
              </li>
            ))}
          </ul>
          {hidden > 0 && (
            <button type="button" onClick={() => setExpanded(true)}
              style={{ marginTop: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: isDark ? '#2dd4bf' : '#0f766e' }}>
              +{hidden} more
            </button>
          )}
        </>
      )}
    </div>
  );
}
